import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { featuresApi } from '../../services/adminApi';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Sortable Item Component
const SortableItem = ({ feature, onEdit, onDelete, onToggleActive }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: feature.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const tableRowStyle = {
    borderBottom: '1px solid #e5e7eb',
    backgroundColor: isDragging ? '#f3f4f6' : 'white',
    cursor: isDragging ? 'grabbing' : 'grab'
  };

  const tableCellStyle = {
    padding: '1rem 1.5rem',
    fontSize: '0.875rem',
    color: '#111827',
    borderBottom: '1px solid #e5e7eb'
  };

  const statusButtonStyle = (isActive) => ({
    display: 'inline-flex',
    alignItems: 'center',
    padding: '0.25rem 0.625rem',
    borderRadius: '9999px',
    fontSize: '0.75rem',
    fontWeight: '500',
    backgroundColor: isActive ? '#dcfce7' : '#fee2e2',
    color: isActive ? '#166534' : '#dc2626',
    border: 'none',
    cursor: 'pointer',
    transition: 'background-color 0.2s, color 0.2s'
  });

  const actionLinkStyle = {
    color: '#2563eb',
    textDecoration: 'none',
    fontSize: '0.875rem',
    fontWeight: '500',
    marginRight: '0.75rem',
    transition: 'color 0.2s'
  };

  const actionButtonStyle = {
    color: '#dc2626',
    textDecoration: 'none',
    border: 'none',
    backgroundColor: 'transparent',
    cursor: 'pointer',
    fontSize: '0.875rem',
    fontWeight: '500',
    transition: 'color 0.2s'
  };

  const dragHandleStyle = {
    cursor: 'grab',
    padding: '0.5rem',
    color: '#6b7280',
    fontSize: '1rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '2rem',
    height: '2rem',
    borderRadius: '0.25rem',
    backgroundColor: '#f9fafb',
    border: '1px solid #e5e7eb',
    transition: 'all 0.2s'
  };

  return (
    <tr 
      ref={setNodeRef} 
      style={style}
    >
      <td style={tableCellStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <div 
            style={dragHandleStyle} 
            title="Sürüklemek için tut"
            {...attributes}
            {...listeners}
          >
            ⋮⋮
          </div>
          <div style={{ fontWeight: '500' }}>
            {feature.title || 'Başlık Yok'}
          </div>
        </div>
      </td>
      <td style={tableCellStyle}>
        <div style={{ maxWidth: '20rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {feature.description || 'Açıklama Yok'}
        </div>
      </td>
      <td style={tableCellStyle}>
        <div>
          {feature.icon || '📄'}
        </div>
      </td>
      <td style={tableCellStyle}>
        <div>
          {feature.sort_order || 0}
        </div>
      </td>
      <td style={tableCellStyle}>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onToggleActive(feature.id, feature.is_active);
          }}
          style={statusButtonStyle(feature.is_active)}
        >
          {feature.is_active ? 'Aktif' : 'Pasif'}
        </button>
      </td>
      <td style={tableCellStyle}>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <Link
            to={`/admin/features/${feature.id}/edit`}
            style={actionLinkStyle}
            onMouseOver={(e) => e.target.style.color = '#1d4ed8'}
            onMouseOut={(e) => e.target.style.color = '#2563eb'}
          >
            Düzenle
          </Link>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDelete(feature.id);
            }}
            style={actionButtonStyle}
            onMouseOver={(e) => e.target.style.color = '#b91c1c'}
            onMouseOut={(e) => e.target.style.color = '#dc2626'}
          >
            Sil
          </button>
        </div>
      </td>
    </tr>
  );
};

const FeaturesList = () => {
  const [features, setFeatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isUpdatingOrder, setIsUpdatingOrder] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    fetchFeatures();
  }, []);

  const fetchFeatures = async () => {
    try {
      setLoading(true);
      console.log('Fetching features...');
      
      const response = await featuresApi.getAll();
      console.log('Full response:', response);
      console.log('Response data:', response.data);
      
      if (response.data.success) {
        const featuresData = response.data.data?.data || response.data.data || [];
        console.log('Features data:', featuresData);
        setFeatures(featuresData);
        setError(null);
      } else {
        console.log('API returned success: false');
        setError('Özellikler yüklenemedi');
      }
    } catch (err) {
      console.error('Features fetch error:', err);
      setError('Özellikler yüklenirken hata oluştu: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bu özelliği silmek istediğinizden emin misiniz?')) {
      return;
    }

    try {
      const response = await featuresApi.delete(id);
      if (response.data.success) {
        setFeatures(features.filter(feature => feature.id !== id));
        alert('Özellik başarıyla silindi');
      } else {
        alert('Özellik silinemedi');
      }
    } catch (err) {
      console.error('Delete error:', err);
      alert('Özellik silinirken hata oluştu');
    }
  };

  const toggleActive = async (id, currentStatus) => {
    try {
      const response = await featuresApi.update(id, { is_active: !currentStatus });
      if (response.data.success) {
        setFeatures(features.map(feature =>
          feature.id === id ? { ...feature, is_active: !currentStatus } : feature
        ));
        alert('Özellik durumu güncellendi');
      } else {
        alert('Özellik durumu güncellenemedi');
      }
    } catch (err) {
      console.error('Toggle error:', err);
      alert('Özellik durumu güncellenirken hata oluştu');
    }
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      const oldIndex = features.findIndex(item => item.id === active.id);
      const newIndex = features.findIndex(item => item.id === over.id);
      
      const newFeatures = arrayMove(features, oldIndex, newIndex);
      setFeatures(newFeatures);

      // Update sort_order for all items
      setIsUpdatingOrder(true);
      try {
        const updatePromises = newFeatures.map((feature, index) => 
          featuresApi.update(feature.id, { sort_order: index + 1 })
        );
        
        await Promise.all(updatePromises);
        
        // Update local state with new sort_order values
        const updatedFeatures = newFeatures.map((feature, index) => ({
          ...feature,
          sort_order: index + 1
        }));
        setFeatures(updatedFeatures);
        
        console.log('Sıralama güncellendi');
      } catch (err) {
        console.error('Sıralama güncellenirken hata:', err);
        alert('Sıralama güncellenirken hata oluştu');
        // Revert on error
        fetchFeatures();
      } finally {
        setIsUpdatingOrder(false);
      }
    }
  };

  const containerStyle = {
    padding: '1.5rem',
    backgroundColor: '#f9fafb',
    minHeight: '100vh'
  };

  const headerStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1.5rem'
  };

  const titleStyle = {
    fontSize: '1.5rem',
    fontWeight: '700',
    color: '#111827',
    margin: 0
  };

  const buttonStyle = {
    backgroundColor: '#3b82f6',
    color: 'white',
    padding: '0.5rem 1rem',
    borderRadius: '0.375rem',
    border: 'none',
    fontSize: '0.875rem',
    fontWeight: '500',
    cursor: 'pointer',
    textDecoration: 'none',
    display: 'inline-block',
    transition: 'background-color 0.2s'
  };

  const loadingStyle = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '16rem',
    padding: '2rem'
  };

  const spinnerStyle = {
    width: '2rem',
    height: '2rem',
    border: '2px solid #e5e7eb',
    borderTop: '2px solid #3b82f6',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite'
  };

  const errorStyle = {
    textAlign: 'center',
    padding: '2rem',
    backgroundColor: 'white',
    borderRadius: '0.5rem',
    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
  };

  const errorTextStyle = {
    color: '#dc2626',
    marginBottom: '1rem'
  };

  const tableStyle = {
    backgroundColor: 'white',
    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
    borderRadius: '0.5rem',
    overflow: 'hidden'
  };

  const tableHeaderStyle = {
    backgroundColor: '#f9fafb',
    padding: '0.75rem 1.5rem',
    textAlign: 'left',
    fontSize: '0.75rem',
    fontWeight: '500',
    color: '#6b7280',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    borderBottom: '1px solid #e5e7eb'
  };

  const tableCellStyle = {
    padding: '1rem 1.5rem',
    fontSize: '0.875rem',
    color: '#111827',
    borderBottom: '1px solid #e5e7eb'
  };

  console.log('FeaturesList render - loading:', loading, 'error:', error, 'features:', features);

  if (loading) {
    return (
      <>
        <Helmet>
          <title>Özellikler - Admin Panel</title>
        </Helmet>
        <div style={containerStyle}>
          <div style={loadingStyle}>
            <div style={spinnerStyle}></div>
            <p style={{ marginTop: '1rem', color: '#6b7280' }}>Özellikler yükleniyor...</p>
          </div>
        </div>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </>
    );
  }

  if (error) {
    return (
      <>
        <Helmet>
          <title>Özellikler - Admin Panel</title>
        </Helmet>
        <div style={containerStyle}>
          <div style={errorStyle}>
            <p style={errorTextStyle}>{error}</p>
            <button 
              onClick={fetchFeatures}
              style={buttonStyle}
              onMouseOver={(e) => e.target.style.backgroundColor = '#2563eb'}
              onMouseOut={(e) => e.target.style.backgroundColor = '#3b82f6'}
            >
              Tekrar Dene
            </button>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Özellikler - Admin Panel</title>
      </Helmet>
      
      <div style={containerStyle}>
        <div style={headerStyle}>
          <h1 style={titleStyle}>Özellikler ({features.length})</h1>
          <Link
            to="/admin/features/create"
            style={buttonStyle}
            onMouseOver={(e) => e.target.style.backgroundColor = '#2563eb'}
            onMouseOut={(e) => e.target.style.backgroundColor = '#3b82f6'}
          >
            Yeni Özellik Ekle
          </Link>
        </div>

        {features.length === 0 ? (
          <div style={errorStyle}>
            <p style={{ color: '#6b7280', marginBottom: '1rem' }}>Henüz özellik eklenmemiş</p>
            <Link
              to="/admin/features/create"
              style={buttonStyle}
              onMouseOver={(e) => e.target.style.backgroundColor = '#2563eb'}
              onMouseOut={(e) => e.target.style.backgroundColor = '#3b82f6'}
            >
              İlk Özelliği Ekle
            </Link>
          </div>
        ) : (
          <div style={tableStyle}>
            <div style={{ overflowX: 'auto' }}>
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      <th style={tableHeaderStyle}>Özellik</th>
                      <th style={tableHeaderStyle}>Açıklama</th>
                      <th style={tableHeaderStyle}>İkon</th>
                      <th style={tableHeaderStyle}>Sıralama</th>
                      <th style={tableHeaderStyle}>Durum</th>
                      <th style={tableHeaderStyle}>İşlemler</th>
                    </tr>
                  </thead>
                  <SortableContext
                    items={features.map(f => f.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    <tbody>
                      {features.map((feature, index) => (
                        <SortableItem
                          key={feature.id}
                          feature={feature}
                          onEdit={() => {}}
                          onDelete={handleDelete}
                          onToggleActive={toggleActive}
                        />
                      ))}
                    </tbody>
                  </SortableContext>
                </table>
              </DndContext>
            </div>
            {isUpdatingOrder && (
              <div style={{
                position: 'fixed',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                color: 'white',
                padding: '1rem 2rem',
                borderRadius: '0.5rem',
                fontSize: '0.875rem',
                fontWeight: '500',
                zIndex: 1000
              }}>
                Sıralama güncelleniyor...
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default FeaturesList;